import { roundCents } from './rounding.js';

// Convert APR (percent) to monthly rate factor.
export function monthlyRate(apr: number): number {
  return apr / 12 / 100;
}

// Annuity-like fixed payment amount for current balance and remaining months.
export function calcFixedPayment(balanceCents: number, monthsRemaining: number, apr: number): number {
  if (monthsRemaining <= 0) return 0;
  const r = monthlyRate(apr);
  const pow = Math.pow(1 + r, monthsRemaining);
  // A rate too small to register in floating point behaves like a zero rate.
  if (r === 0 || pow <= 1) {
    return roundCents(balanceCents / monthsRemaining);
  }
  const payment = balanceCents * (r * pow) / (pow - 1);
  return roundCents(payment);
}

// Constant principal share of a declining-payment loan, rounded to cents.
// With no months left the whole balance is due.
export function calcDecliningPrincipalPart(balanceCents: number, monthsRemaining: number): number {
  if (monthsRemaining <= 0) return balanceCents;
  return roundCents(balanceCents / monthsRemaining);
}

// Declining payment: constant principal part plus interest on remaining balance.
// With no months left the payment settles the balance plus interest.
export function calcDecliningPayment(balanceCents: number, monthsRemaining: number, apr: number): number {
  const interest = roundCents(balanceCents * monthlyRate(apr));
  return calcDecliningPrincipalPart(balanceCents, monthsRemaining) + interest;
}

export type MonthSettlement = {
  scheduledPayment: number;   // cents actually charged as the scheduled payment
  interestPortion: number;    // cents
  principalPortion: number;   // cents; negative when interest exceeds the payment
  balanceAfter: number;       // cents; before any overpayment
};

/**
 * Split one month's scheduled payment into interest and principal and apply it
 * to the balance. Two rules adjust the nominal payment:
 * - Settlement: in the final contractual month, or when the nominal payment
 *   would overshoot the debt, the payment becomes exactly balance + interest and
 *   the balance drops to zero. Rounding drift can never leave a residual that
 *   would need an extra month.
 * - Capitalisation: when the nominal payment does not even cover the interest,
 *   the shortfall is added to the balance instead of being forgiven, and
 *   principalPortion is negative by that amount.
 */
export function settleScheduledPayment(
  balanceCents: number,
  nominalPaymentCents: number,
  apr: number,
  isFinalMonth: boolean,
): MonthSettlement {
  const interestPortion = roundCents(balanceCents * monthlyRate(apr));
  return settlePaymentWithInterest(balanceCents, nominalPaymentCents, interestPortion, isFinalMonth);
}

/** The same settlement rules with an already rounded, externally calculated interest amount. */
export function settlePaymentWithInterest(
  balanceCents: number,
  nominalPaymentCents: number,
  interestPortion: number,
  isFinalMonth: boolean,
): MonthSettlement {
  const payoff = balanceCents + interestPortion;
  const scheduledPayment = isFinalMonth || payoff <= nominalPaymentCents ? payoff : nominalPaymentCents;
  const principalPortion = scheduledPayment - interestPortion;
  return {
    scheduledPayment,
    interestPortion,
    principalPortion,
    balanceAfter: balanceCents - principalPortion,
  };
}

/**
 * Remaining term after a reduce_term overpayment on the monthly model: payments of the
 * retained `paymentCents` at `apr` until the debt is gone (at most `limit`). A later rate
 * change then recalculates the payment over this shortened term, so the term cut is kept.
 */
export function monthlyRemainingTerm(balanceCents: number, paymentCents: number, apr: number, limit: number): number {
  let balance = balanceCents;
  for (let count = 1; count <= limit; count++) {
    const interest = roundCents(balance * monthlyRate(apr));
    if (balance + interest <= paymentCents) return count;
    balance -= paymentCents - interest;
  }
  return limit;
}
